import { useEffect } from 'react';
import { getRealtime } from '@/features/chat/api';
import { desktopBridge } from '@/features/desktop/bridge';
import { presenceActions } from '../presenceStore';
import { applySystemActivity, reportPresence } from '../systemActivity';

const TICK_MS = 15_000;
/** Ignore input events that arrive within this window — one timestamp write is enough. */
const INPUT_THROTTLE_MS = 1000;
/**
 * MOUSE MOVEMENT IS INPUT, and it was the one kind missing.
 *
 * `pointermove` is listed apart from the rest because it is the only entry here
 * that fires continuously — up to once per display refresh, so 60–144 times a
 * second — and because it needs a check the others do not (see `onPointerMove`).
 * The owner asked for "mouse activity movement" by name; Teams counts it, and
 * without it moving the mouse across this very window did not reset the idle
 * clock. Only clicks, keys, wheel and touch did.
 */
const INPUT_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Drives every automatic presence transition from ONE 15s timer plus passive,
 * throttled input listeners. No React state is touched on input — the store is
 * only written when the resolved state actually changes.
 *
 * WHAT IS ACTUALLY MEASURED HERE, AND ON WHICH PLATFORM. The comment that used
 * to sit here said Away was measured "exactly as Teams measures it". That was
 * FALSE, and it is the reason the complaint went unnoticed for so long:
 *
 *   IN A BROWSER this measures DOM input delivered to THIS DOCUMENT and nothing
 *   else. It cannot see the machine. A user who alt-tabs to another application
 *   and works there for ten minutes delivers no event to this page at all, and
 *   this clock ages them to Away while they type. There is no fix available in
 *   a tab: the web platform exposes no system-wide idle time (`IdleDetector` is
 *   Chromium-only, permission-gated and absent from an ordinary page), so this
 *   is the honest ceiling of what a browser client can know, and the listeners
 *   below remain exactly what they were.
 *
 *   ON THE DESKTOP the shell answers the real question, and it is the ONLY
 *   answer. Electron's `powerMonitor.getSystemIdleTime()` is GetLastInputInfo
 *   on Windows — the same fact Microsoft Teams uses — and reports the last
 *   input to ANY application, plus a distinct `locked` state and a resume
 *   signal. Those reports arrive through
 *   `features/desktop/hooks/useSystemActivity.ts`. That is what makes working
 *   in Excel with this window minimised keep you Available.
 *
 *   A DOM EVENT IS NOT EVIDENCE THERE, ONLY A REASON TO ASK. Both clocks only
 *   move forward, so a DOM event stamped "now" used to beat the OS saying "the
 *   last input to this machine was fifty minutes ago" - and every event the OS
 *   never saw (the first pointer event a reloaded document receives under a
 *   resting cursor, the window moving under it) bought five more minutes of
 *   Available for an empty desk. On the desktop an event now asks the shell for
 *   a FRESH reading instead (`bridge.systemActivity()` polls GetLastInputInfo
 *   at that moment): real input is in it within one IPC round trip, and input
 *   the OS never saw changes nothing. A shell that cannot answer falls back to
 *   the DOM, which is all a browser ever has.
 *
 * Hiding the window is reported so the transport can claim heartbeat grace, but
 * it does not age the idle clock: Teams makes you Away for going quiet or
 * locking your screen, never for alt-tabbing.
 */
export function useActivityMonitor(): void {
  useEffect(() => {
    let lastSeen = 0;
    let lastX = Number.NaN;
    let lastY = Number.NaN;

    /** The DOM's own vote - a browser's only one, and the desktop's fallback. */
    const countDomInput = (): void => {
      presenceActions.markActivity();
      /* BOTH CLOCKS, always together: this one is the `idleMs` the client puts
         on every `ping` and the only idle signal the gateway ever sees. */
      getRealtime().noteActivity();
      /*
       * AND THE FRAME, HERE RATHER THAN ON THE NEXT BEAT.
       *
       * `reportPresence` sends only when its answer differs from the one
       * already sent, so this costs nothing on the 3,600 events an hour that
       * change nothing and one frame on the two transitions that do: coming
       * BACK from Away, and a document's FIRST measurement of a human, which is
       * the moment it stops being entitled to say nothing at all. Both used to
       * wait up to a full 15 s beat while the person watched their own chip.
       *
       * It is throttled by `lastSeen` in `onInput` at one call per second, and
       * the store read behind it is a field compare.
       */
      reportPresence();
    };

    const onInput = (): void => {
      const now = Date.now();
      if (now - lastSeen < INPUT_THROTTLE_MS) return;
      lastSeen = now;
      /* THE DESKTOP ASKS THE MACHINE. See the header: only the OS may vouch for
         a human there, and this event is merely the cue to ask it now rather
         than at the next 2 s poll. */
      const shell = desktopBridge();
      if (shell?.systemActivity !== undefined) {
        void shell.systemActivity().then(
          (report) => {
            if (report === null) {
              countDomInput();
              return;
            }
            applySystemActivity(report);
            reportPresence();
          },
          () => countDomInput(),
        );
        return;
      }
      countDomInput();
    };

    /**
     * THE MOUSE ACTUALLY MOVED — not the page underneath a still one.
     *
     * Two guards, and both earn their place:
     *
     *   THE COORDINATES. Chromium dispatches a synthetic `pointermove` when
     *     content scrolls or animates under a stationary cursor, so that hover
     *     targets stay correct. Those carry the SAME clientX/clientY. Counting
     *     them would let a spinner, a marquee or an auto-scrolling feed report
     *     a human who went home — the precise failure this whole change exists
     *     to remove, arriving from the other direction.
     *
     *   THE THROTTLE, which `onInput` already applies and which covers this
     *     event without help: `lastSeen` starts at 0, so `now - 0 < 1000` is
     *     false for any real timestamp and THE FIRST MOVE IS NEVER DROPPED —
     *     the property that matters, because that first move is what makes a
     *     returning user Available. After it, one store write per second is
     *     all a 300 000 ms threshold could possibly need, and the other 143
     *     events in that second cost one `Date.now()` and a compare each, on a
     *     passive listener that can never delay a scroll.
     */
    const onPointerMove = (event: PointerEvent): void => {
      if (event.clientX === lastX && event.clientY === lastY) return;
      lastX = event.clientX;
      lastY = event.clientY;
      onInput();
    };

    const onVisibility = (): void => {
      const hidden = document.visibilityState === 'hidden';
      presenceActions.setTabHidden(hidden);
      if (!hidden) presenceActions.tick();
    };

    const onLeaving = (): void => {
      presenceActions.setFlags({ inCall: false, inMeeting: false, presenting: false, screenSharing: false });
    };

    for (const event of INPUT_EVENTS) {
      window.addEventListener(event, onInput, { passive: true });
    }
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onLeaving);

    /**
     * A CALL IN ANOTHER WINDOW IS A FACT ABOUT A WINDOW, AND NEITHER CLOCK IS
     * TOLD ABOUT IT ANY MORE.
     *
     * `if (shellActivityIsLive()) getRealtime().noteActivity();` stood on this
     * line, and it is the same defect as the one removed from `presenceStore`'s
     * `tick` - one source further out, and on the clock that actually reaches
     * the gateway. It restamped `idleMs` to zero every 15 s from a FLAG, so no
     * OS reading could ever contradict it: executed, fifty minutes of measured
     * absence became `ping.idleMs = 25s` and the server flipped away ->
     * AVAILABLE with nobody at the machine. A crashed call renderer or a
     * dropped IPC leaves that flag set, and it stays set for the five minutes
     * after every call ends.
     *
     * The two clocks stay in step because NEITHER is pinned: the person's chip
     * is protected one tier up by `resolveEffectiveState` branch 5, which
     * returns `in_a_call` above the auto state, and the honest idle time is
     * still measured underneath it by the shell for when the call is over.
     */
    const beat = (): void => {
      presenceActions.tick();
      /*
       * AND TELL THE SERVER WHEN THE ANSWER CHANGES.
       *
       * Crossing the away threshold is a matter of the CLOCK, not of an event:
       * nothing fires at five minutes, so nothing used to be sent and the
       * gateway learned it from the next `ping` - up to 25 s late, and never at
       * all for a client whose evidence came from a source the ping does not
       * carry. `reportPresence` sends only when its answer differs from the one
       * already sent, so this is a frame per real transition and nothing on the
       * 148 beats an hour in between.
       */
      reportPresence();
    };

    beat();
    const timer = window.setInterval(beat, TICK_MS);

    return () => {
      for (const event of INPUT_EVENTS) window.removeEventListener(event, onInput);
      window.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onLeaving);
      window.clearInterval(timer);
    };
  }, []);
}
